import React, { useRef, useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { Eraser } from "@phosphor-icons/react";

/**
 * Simple signature pad backed by <canvas>. Emits a PNG data URL via onChange.
 * Supports mouse and touch. Use with a ref to call `.clear()` externally.
 */
const SignaturePad = forwardRef(function SignaturePad({ onChange, testId, height = 140 }, ref) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * dpr;
    c.height = height * dpr;
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0F172A";
    ctx.lineWidth = 2;
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, rect.width, height);
  }, [height]);

  const pos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - r.left, y: t.clientY - r.top };
  };
  const start = (e) => {
    e.preventDefault();
    const p = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    setDrawing(true);
    setEmpty(false);
  };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const p = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  };
  const end = () => {
    if (!drawing) return;
    setDrawing(false);
    if (onChange) onChange(canvasRef.current.toDataURL("image/png"));
  };

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.restore();
    setEmpty(true);
    if (onChange) onChange("");
  };
  useImperativeHandle(ref, () => ({ clear, isEmpty: () => empty }));

  return (
    <div className="relative">
      <canvas
        ref={canvasRef}
        data-testid={testId}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        style={{ width: "100%", height, touchAction: "none" }}
        className="rounded-sm border border-white/15 bg-white cursor-crosshair"
      />
      <button
        type="button"
        onClick={clear}
        data-testid={testId ? `${testId}-clear` : undefined}
        className="absolute top-2 right-2 flex items-center gap-1 text-[10px] font-mono-data uppercase tracking-widest px-2 py-1 rounded-sm bg-black/60 text-white hover:bg-black"
      >
        <Eraser size={11} /> Limpiar
      </button>
    </div>
  );
});

export default SignaturePad;
